import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Skeleton, Text } from '../../../../components';
import { useDebouncedValue } from '../../useDebouncedValue';
import type { SiteSeoUrl, SiteSeoUrls } from '../../seo-types';
import type { SiteSeoResult } from '../../site-seo-api';
import { ErrorRow, ErrorText, List, Message, Option, OptionPath, Root, SkeletonRow } from './UrlPicker.styles';

/** The site's real urls (`GET …/site-seo/urls`): `q` a substring of path or title, `prefix` a path start. */
export type SearchUrls = (
  q: { q?: string; prefix?: string; limit?: number },
  signal?: AbortSignal,
) => Promise<SiteSeoResult<SiteSeoUrls>>;

export type UrlPickerProps = {
  searchUrls: SearchUrls;
  /** Paths already in the document: their rows are hidden. */
  exclude: ReadonlySet<string>;
  onPick: (url: SiteSeoUrl) => void;
  /** The picked path, marked selected in the list. */
  selected?: string;
};

export const SEARCH_DEBOUNCE_MS = 300;
const MAX_ROWS = 20;
const SKELETON_ROWS = 3;

type Answer = { term: string; attempt: number; urls: SiteSeoUrl[] | null };

/**
 * Search the site's real pages: the query is sent once the owner pauses typing (each new search aborts the one in
 * flight, whose answer is dropped), and up to 20 rows "title · path" are listed, minus the paths already in the
 * document. States: an idle hint, a loading skeleton, no match, and a failed search with Retry.
 */
export const UrlPicker = ({ searchUrls, exclude, onPick, selected }: UrlPickerProps): React.ReactElement => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const [attempt, setAttempt] = React.useState(0);
  const [answer, setAnswer] = React.useState<Answer | null>(null);
  const term = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  // The caller may pass a new function each render; only a new term or a retry searches again.
  const searchRef = React.useRef(searchUrls);
  searchRef.current = searchUrls;

  React.useEffect(() => {
    if (term === '') return undefined;
    const controller = new AbortController();
    searchRef
      .current({ q: term, limit: MAX_ROWS }, controller.signal)
      .catch((): SiteSeoResult<SiteSeoUrls> => ({ ok: false, reason: 'failed' }))
      .then((result) => {
        if (controller.signal.aborted) return; // a newer search replaced it
        setAnswer({ term, attempt, urls: result.ok ? result.value.urls : null });
      });
    return () => controller.abort();
  }, [term, attempt]);

  const typed = query.trim();
  const settled = answer !== null && answer.term === typed && answer.term === term && answer.attempt === attempt;
  const loading = typed !== '' && !settled;
  const rows = settled && answer.urls ? answer.urls.filter((u) => !exclude.has(u.path)) : [];

  return (
    <Root>
      <Field label={t("Search your site's pages")} value={query} onChange={setQuery} />
      {typed === '' ? <Message text="Type to search your site's pages" tag="span" /> : null}
      {loading || rows.length > 0 ? (
        <List role="listbox" aria-label={t("Your site's pages")} aria-busy={loading ? 'true' : 'false'}>
          {loading
            ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <SkeletonRow key={i} aria-hidden="true">
                  <Skeleton width="70%" height={14} />
                </SkeletonRow>
              ))
            : rows.map((url) => (
                <Option
                  key={url.path}
                  role="option"
                  tabIndex={0}
                  aria-selected={selected === url.path}
                  $selected={selected === url.path}
                  onClick={() => onPick(url)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onPick(url);
                  }}
                >
                  {url.title !== '' ? <Text text={`${url.title} · `} raw tag="span" /> : null}
                  <OptionPath text={url.path} raw tag="span" />
                </Option>
              ))}
        </List>
      ) : null}
      {settled && answer.urls !== null && rows.length === 0 ? (
        <Message text="No page matches {{q}}" placeholders={{ q: typed }} tag="span" />
      ) : null}
      {settled && answer.urls === null ? (
        <ErrorRow>
          <ErrorText text="Couldn't search right now" tag="span" />
          <Button label={t('Retry')} variant="outline" onClick={() => setAttempt((a) => a + 1)} />
        </ErrorRow>
      ) : null}
    </Root>
  );
};

export default UrlPicker;
